import React, { useState, useEffect, useReducer } from "react";
import {
  makeStyles,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip
} from "@material-ui/core";
import {
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from "@material-ui/icons";
import { ChromePicker } from "react-color";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../../components/ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  typeRow: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  categoryRow: {
    backgroundColor: theme.palette.action.hover,
    paddingLeft: theme.spacing(4),
  },
  colorBox: {
    width: 24,
    height: 24,
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
  },
  addCategoryButton: {
    marginLeft: theme.spacing(2),
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_TYPES") {
    return [...action.payload];
  }
  if (action.type === "UPDATE_TYPE") {
    return state.map((type) =>
      type.id === action.payload.id ? action.payload : type
    );
  }
  if (action.type === "DELETE_TYPE") {
    return state.filter((type) => type.id !== action.payload);
  }
  if (action.type === "ADD_TYPE") {
    return [action.payload, ...state];
  }
  return state;
};

const ClassificationSettings = () => {
  const classes = useStyles();

  const [types, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingType, setDeletingType] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [expandedTypes, setExpandedTypes] = useState({});

  // Form states
  const [typeName, setTypeName] = useState("");
  const [typeColor, setTypeColor] = useState("#4caf50");
  const [typeOrder, setTypeOrder] = useState(0);
  const [categoryName, setCategoryName] = useState("");
  const [categoryOrder, setCategoryOrder] = useState(0);
  const [parentTypeId, setParentTypeId] = useState(null);

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/classification-types");
      dispatch({ type: "LOAD_TYPES", payload: data });
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTypeModal = (type = null) => {
    if (type) {
      setSelectedType(type);
      setTypeName(type.name);
      setTypeColor(type.color);
      setTypeOrder(type.order);
    } else {
      setSelectedType(null);
      setTypeName("");
      setTypeColor("#4caf50");
      setTypeOrder(types.length);
    }
    setTypeModalOpen(true);
  };

  const handleCloseTypeModal = () => {
    setTypeModalOpen(false);
    setSelectedType(null);
    setTypeName("");
    setTypeColor("#4caf50");
    setTypeOrder(0);
  };

  const handleSaveType = async () => {
    try {
      if (!typeName.trim()) {
        toast.error(i18n.t("classificationSettings.errors.nameRequired"));
        return;
      }

      if (selectedType) {
        // Update
        const { data } = await api.put(`/classification-types/${selectedType.id}`, {
          name: typeName,
          color: typeColor,
          order: typeOrder,
        });
        dispatch({ type: "UPDATE_TYPE", payload: { ...selectedType, ...data } });
        toast.success(i18n.t("classificationSettings.success.typeUpdated"));
      } else {
        // Create
        const { data } = await api.post("/classification-types", {
          name: typeName,
          color: typeColor,
          order: typeOrder,
        });
        dispatch({ type: "ADD_TYPE", payload: { ...data, categories: [] } });
        toast.success(i18n.t("classificationSettings.success.typeCreated"));
      }
      handleCloseTypeModal();
      fetchTypes(); // Refresh to get updated data
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteType = async (typeId) => {
    try {
      await api.delete(`/classification-types/${typeId}`);
      dispatch({ type: "DELETE_TYPE", payload: typeId });
      toast.success(i18n.t("classificationSettings.success.typeDeleted"));
      setConfirmModalOpen(false);
      setDeletingType(null);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenCategoryModal = (type, category = null) => {
    setParentTypeId(type.id);
    if (category) {
      setSelectedCategory(category);
      setCategoryName(category.name);
      setCategoryOrder(category.order);
    } else {
      setSelectedCategory(null);
      setCategoryName("");
      setCategoryOrder(type.categories ? type.categories.length : 0);
    }
    setCategoryModalOpen(true);
  };

  const handleCloseCategoryModal = () => {
    setCategoryModalOpen(false);
    setSelectedCategory(null);
    setCategoryName("");
    setCategoryOrder(0);
    setParentTypeId(null);
  };

  const handleSaveCategory = async () => {
    try {
      if (!categoryName.trim()) {
        toast.error(i18n.t("classificationSettings.errors.nameRequired"));
        return;
      }

      if (selectedCategory) {
        // Update
        await api.put(`/classification-categories/${selectedCategory.id}`, {
          name: categoryName,
          order: categoryOrder,
        });
        toast.success(i18n.t("classificationSettings.success.categoryUpdated"));
      } else {
        // Create
        await api.post("/classification-categories", {
          typeId: parentTypeId,
          name: categoryName,
          order: categoryOrder,
        });
        toast.success(i18n.t("classificationSettings.success.categoryCreated"));
      }
      handleCloseCategoryModal();
      fetchTypes(); // Refresh to get updated data
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      await api.delete(`/classification-categories/${categoryId}`);
      toast.success(i18n.t("classificationSettings.success.categoryDeleted"));
      setConfirmModalOpen(false);
      setDeletingCategory(null);
      fetchTypes(); // Refresh to get updated data
    } catch (err) {
      toastError(err);
    }
  };

  const toggleTypeExpand = (typeId) => {
    setExpandedTypes((prev) => ({
      ...prev,
      [typeId]: !prev[typeId],
    }));
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("classificationSettings.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleOpenTypeModal()}
            startIcon={<AddIcon />}
          >
            {i18n.t("classificationSettings.buttons.addType")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 50 }}></TableCell>
              <TableCell>{i18n.t("classificationSettings.table.name")}</TableCell>
              <TableCell style={{ width: 100 }}>{i18n.t("classificationSettings.table.color")}</TableCell>
              <TableCell style={{ width: 100 }}>{i18n.t("classificationSettings.table.order")}</TableCell>
              <TableCell align="right" style={{ width: 150 }}>
                {i18n.t("classificationSettings.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {types.map((type) => (
              <React.Fragment key={type.id}>
                <TableRow className={classes.typeRow}>
                  <TableCell>
                    <IconButton size="small" onClick={() => toggleTypeExpand(type.id)}>
                      {expandedTypes[type.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" style={{ fontWeight: 500 }}>
                      {type.name}
                      <Chip
                        size="small"
                        label={`${type.categories?.length || 0} ${i18n.t("classificationSettings.categories")}`}
                        style={{ marginLeft: 8 }}
                      />
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <div
                      className={classes.colorBox}
                      style={{ backgroundColor: type.color }}
                    />
                  </TableCell>
                  <TableCell>{type.order}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenCategoryModal(type)}
                      className={classes.addCategoryButton}
                    >
                      <AddIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenTypeModal(type)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setDeletingType(type);
                        setConfirmModalOpen(true);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
                {expandedTypes[type.id] &&
                  type.categories?.map((category) => (
                    <TableRow key={category.id} className={classes.categoryRow}>
                      <TableCell></TableCell>
                      <TableCell>↳ {category.name}</TableCell>
                      <TableCell></TableCell>
                      <TableCell>{category.order}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenCategoryModal(type, category)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setDeletingCategory(category);
                            setConfirmModalOpen(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Type Modal */}
      <Dialog open={typeModalOpen} onClose={handleCloseTypeModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedType
            ? i18n.t("classificationSettings.modal.editType")
            : i18n.t("classificationSettings.modal.addType")}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            label={i18n.t("classificationSettings.modal.name")}
            value={typeName}
            onChange={(e) => setTypeName(e.target.value)}
            variant="outlined"
            margin="dense"
            fullWidth
          />
          <div style={{ marginTop: 16, marginBottom: 16 }}>
            <Typography variant="body2" gutterBottom>
              {i18n.t("classificationSettings.modal.color")}
            </Typography>
            <ChromePicker
              color={typeColor}
              onChange={(color) => setTypeColor(color.hex)}
              disableAlpha
            />
          </div>
          <TextField
            label={i18n.t("classificationSettings.modal.order")}
            type="number"
            value={typeOrder}
            onChange={(e) => setTypeOrder(parseInt(e.target.value))}
            variant="outlined"
            margin="dense"
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTypeModal}>
            {i18n.t("classificationSettings.modal.cancel")}
          </Button>
          <Button onClick={handleSaveType} color="primary" variant="contained">
            {i18n.t("classificationSettings.modal.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Modal */}
      <Dialog open={categoryModalOpen} onClose={handleCloseCategoryModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedCategory
            ? i18n.t("classificationSettings.modal.editCategory")
            : i18n.t("classificationSettings.modal.addCategory")}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            label={i18n.t("classificationSettings.modal.name")}
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
            variant="outlined"
            margin="dense"
            fullWidth
          />
          <TextField
            label={i18n.t("classificationSettings.modal.order")}
            type="number"
            value={categoryOrder}
            onChange={(e) => setCategoryOrder(parseInt(e.target.value))}
            variant="outlined"
            margin="dense"
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCategoryModal}>
            {i18n.t("classificationSettings.modal.cancel")}
          </Button>
          <Button onClick={handleSaveCategory} color="primary" variant="contained">
            {i18n.t("classificationSettings.modal.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Modal */}
      <ConfirmationModal
        title={
          deletingType
            ? i18n.t("classificationSettings.confirmModal.deleteTypeTitle")
            : i18n.t("classificationSettings.confirmModal.deleteCategoryTitle")
        }
        open={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setDeletingType(null);
          setDeletingCategory(null);
        }}
        onConfirm={() => {
          if (deletingType) {
            handleDeleteType(deletingType.id);
          } else if (deletingCategory) {
            handleDeleteCategory(deletingCategory.id);
          }
        }}
      >
        {deletingType
          ? i18n.t("classificationSettings.confirmModal.deleteTypeMessage", {
              name: deletingType.name,
            })
          : i18n.t("classificationSettings.confirmModal.deleteCategoryMessage", {
              name: deletingCategory?.name,
            })}
      </ConfirmationModal>
    </MainContainer>
  );
};

export default ClassificationSettings;
